import type { HttpEvent, HttpRequest } from '@angular/common/http';
import { HttpInterceptorFn } from '@angular/common/http';
import { Observable, finalize, share } from 'rxjs';

/**
 * Coalesces identical GETs that are in flight at the same moment into one
 * network request.
 *
 * The public album page mounts a parent (state banner) and a child (gallery)
 * that each load `GET /albums/{id}` on init, and the admin album pages have the
 * same shape for `transitions` — two subscribers, one answer, so the second
 * request was pure duplication. Keyed on method + full URL + the Authorization
 * header, so different principals never share a response; entries live only
 * while the request is in flight (no caching, no staleness), which is exactly
 * the window in which the duplicates occur.
 *
 * Requests that report progress events opt out, as does anything non-GET.
 */
const inFlight = new Map<string, Observable<HttpEvent<unknown>>>();

function keyFor(req: HttpRequest<unknown>): string {
    return `${req.method} ${req.urlWithParams} ${req.headers.get('Authorization') ?? ''}`;
}

export const dedupeGetInterceptor: HttpInterceptorFn = (req, next) => {
    if (req.method !== 'GET' || req.reportProgress) {
        return next(req);
    }
    const key = keyFor(req);
    const existing = inFlight.get(key);
    if (existing) {
        return existing;
    }
    const shared = next(req).pipe(
        finalize(() => inFlight.delete(key)),
        share({ resetOnRefCountZero: false, resetOnComplete: false, resetOnError: false })
    );
    inFlight.set(key, shared);
    return shared;
};
